import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Article } from 'src/app/interfaces/article';
import { ApplicationService } from 'src/app/services/application.service';
import { ArticleService } from 'src/app/services/article.service';
import { SearchService } from 'src/app/services/search.service';

@Component({
  selector: 'app-recommendations-preview',
  templateUrl: './recommendations-preview.component.html',
  styleUrls: ['./recommendations-preview.component.scss'],
  standalone: false,
})
export class RecommendationsPreviewComponent implements OnInit {
  topArticles: Article[] = [];
  recommendedArticles: Article[] = [];
  popularArticles: Article[] = [];

  topIndex = 0;
  recommendedIndex = 0;
  popularIndex = 0;

  companyGroupCode = '';

  constructor(
    private articleService: ArticleService,
    private applicationService: ApplicationService,
    private searchService: SearchService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.companyGroupCode = this.applicationService.getSelectCompanyGroupCode();
    this.loadAll();
  }

  loadAll(): void {
    forkJoin([
      this.articleService.getRecommendations('TOP'),
      this.articleService.getRecommendations('RECOMMENDED'),
      this.articleService.getRecommendations('POPULAR'),
    ]).subscribe(([top, recommended, popular]: [any, any, any]) => {
      this.topArticles = this.extractArticles(top);
      this.recommendedArticles = this.extractArticles(recommended);
      this.popularArticles = this.extractArticles(popular);

      const all = [...this.topArticles, ...this.recommendedArticles, ...this.popularArticles];
      if (all.length) {
        this.searchService.getNettPrices(all);
      }
    });
  }

  private extractArticles(response: any): Article[] {
    const result = response?.result ?? response;

    if (Array.isArray(result)) {
      return result as Article[];
    }

    if (Array.isArray(result?.product_overview_items)) {
      const overviewItems = result.product_overview_items as Article[];

      // Merge metadata from recommended_article when available.
      if (Array.isArray(result?.recommended_article) && result.recommended_article.length) {
        const metaByArticleCode = new Map<string, any>(
          result.recommended_article
            .filter((entry: any) => !!entry?.article_code)
            .map((entry: any) => [entry.article_code, entry]),
        );

        return overviewItems.map((article: Article) => ({
          ...article,
          previously_bought: metaByArticleCode.get(article.single_article_code)?.previously_bought ?? false,
        }));
      }

      return overviewItems;
    }

    return [];
  }

  isPreviouslyBought(article: Article | undefined): boolean {
    return !!(article as any)?.previously_bought;
  }

  prev(section: 'top' | 'recommended' | 'popular'): void {
    if (section === 'top') {
      this.topIndex = this.topIndex > 0 ? this.topIndex - 1 : this.topArticles.length - 1;
    } else if (section === 'recommended') {
      this.recommendedIndex = this.recommendedIndex > 0 ? this.recommendedIndex - 1 : this.recommendedArticles.length - 1;
    } else {
      this.popularIndex = this.popularIndex > 0 ? this.popularIndex - 1 : this.popularArticles.length - 1;
    }
  }

  next(section: 'top' | 'recommended' | 'popular'): void {
    if (section === 'top') {
      if (!this.topArticles.length) { return; }
      this.topIndex = (this.topIndex + 1) % this.topArticles.length;
    } else if (section === 'recommended') {
      if (!this.recommendedArticles.length) { return; }
      this.recommendedIndex = (this.recommendedIndex + 1) % this.recommendedArticles.length;
    } else {
      if (!this.popularArticles.length) { return; }
      this.popularIndex = (this.popularIndex + 1) % this.popularArticles.length;
    }
  }

  showArticleDetails(article: Article): void {
    const url = `/${this.companyGroupCode}/articleDetails/${article.id}`;
    this.router
      .navigateByUrl(`/${this.applicationService.getSelectCompanyGroupCode()}/RefreshComponent`, { skipLocationChange: true })
      .then(() => {
        this.router.navigate([
          url,
        ]);
      });
  }
}
